"use client";

import { createContext, FC, ReactNode, useContext, useState } from "react";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";

type DialogOpts = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

const ConfirmDialogContext = createContext({
  isOpen: false,
  title: "Confirm",
  message: "",
  openDialog: (_: DialogOpts, __: ((val: boolean) => void) | null) => { },
  openDialogAsync: (_: DialogOpts) => new Promise(() => { }),
  closeDialog: (_: boolean) => { },
});

export const useConfirmDialogContext = () => {
  return useContext(ConfirmDialogContext);
};

export const ConfirmDialogProvider: FC<{ children: ReactNode }> = (
  { children },
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("Are you sure?");
  const [confirmText, setConfirmText] = useState("Confirm");
  const [cancelText, setCancelText] = useState("Cancel");
  const [title, setTitle] = useState("Confirm");
  const [onResolve, setOnResolve] = useState<((val: boolean) => void) | null>(
    null,
  );
  const [resolveAsync, setResolveAsync] = useState<
    ((val: boolean) => void) | null
  >(
    null,
  );

  const openDialog = (
    { title, message, confirmText, cancelText }: DialogOpts,
    newOnResolve: ((val: boolean) => void) | null,
  ) => {
    setIsOpen(true);
    setTitle(title || "Confirm");
    setMessage(message || "Are you sure?");
    setConfirmText(confirmText || "Confirm");
    setCancelText(cancelText || "Cancel");
    setOnResolve(newOnResolve);
  };

  const openDialogAsync = (
    opts: DialogOpts,
  ) => {
    const promise = new Promise<boolean>((resolve) => {
      const resolveFunc = (val: boolean) => {
        resolve(val);
      };
      setResolveAsync(() => (val: boolean) => resolveFunc(val));
    });

    openDialog(opts, null);

    return promise;
  };

  const closeDialog = (confirmed: boolean) => {
    setIsOpen(false);

    if (resolveAsync) {
      resolveAsync(confirmed);
    } else if (onResolve) {
      onResolve(confirmed);
    }

    setOnResolve(null);
    setResolveAsync(null);
  };

  const contextValue = {
    isOpen,
    title,
    message,
    openDialog,
    openDialogAsync,
    closeDialog,
  };

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog(false);
          } else {
            throw new DOMException(
              "Confirm dialog should not be opened directly",
            );
          }
        }}
      >
        <DialogContent className="md:max-w-[425px] sm:max-w-[425px] sm:min-w-[400px]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="w-full grid grid-cols-2">
            <Button
              type="submit"
              className="h-[40px]"
              variant={"outline"}
              onClick={() => closeDialog(false)}
            >
              {cancelText}
            </Button>
            <Button
              className="h-[40px]"
              type="submit"
              onClick={() => closeDialog(true)}
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {children}
    </ConfirmDialogContext.Provider>
  );
};
